import { useMemo } from 'react';
import { GitBranch, Home } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { Commit, UserBranch } from '../../types/api';

interface BranchData {
  name: string;
  commitCount: number;
  sortOrder: number;
  commitSortDirection: 'asc' | 'desc';
}

interface BranchHighlightsProps {
  commits: Commit[];
  userBranches?: UserBranch[];
  selectedBranch: string | null;
  onSelectBranch: (branch: string | null) => void;
}

export default function BranchHighlights({
  commits,
  userBranches,
  selectedBranch,
  onSelectBranch,
}: BranchHighlightsProps) {
  const branches = useMemo<BranchData[]>(() => {
    // Prefer server-side branch data if available (accurate counts)
    if (userBranches && userBranches.length > 0) {
      return userBranches
        .filter((b) => b.branch !== 'main') // Don't count main separately - it's the "all" view
        .map((b) => ({
          name: b.branch,
          commitCount: b.commitCount,
          sortOrder: b.sortOrder,
          commitSortDirection: b.commitSortDirection,
        }))
        .sort((a, b) => a.sortOrder - b.sortOrder); // Sort by user-defined order
    }

    // Fallback to calculating from loaded commits (legacy behavior)
    const branchMap = new Map<string, number>();

    commits.forEach((commit) => {
      const branch = commit.branch || 'main';
      // Don't count main separately - it's the "all" view
      if (branch !== 'main') {
        branchMap.set(branch, (branchMap.get(branch) || 0) + 1);
      }
    });

    // Sort by commit count descending (legacy fallback)
    return Array.from(branchMap.entries())
      .map(([name, commitCount]) => ({
        name,
        commitCount,
        sortOrder: 999999,
        commitSortDirection: 'desc' as const,
      }))
      .sort((a, b) => b.commitCount - a.commitCount);
  }, [commits, userBranches]);

  // Always show main, plus any other branches
  const hasOtherBranches = branches.length > 0;

  return (
    <div className="flex flex-wrap gap-2">
      {/* Main/Home button - always visible, shows all commits */}
      <button
        onClick={() => onSelectBranch(null)}
        className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
          selectedBranch === null
            ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
            : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]'
        )}
      >
        <Home className="h-3.5 w-3.5" />
        <span className="font-mono">main</span>
      </button>

      {/* Other branches */}
      {hasOtherBranches && branches.map((branch) => (
        <button
          key={branch.name}
          onClick={() => onSelectBranch(branch.name)}
          className={cn(
            'inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-colors',
            selectedBranch === branch.name
              ? 'bg-[hsl(var(--primary))] text-[hsl(var(--primary-foreground))]'
              : 'bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--accent))] hover:text-[hsl(var(--accent-foreground))]'
          )}
        >
          <GitBranch className="h-3.5 w-3.5" />
          <span className="font-mono">{branch.name}</span>
          <span className="text-xs opacity-70">({branch.commitCount})</span>
        </button>
      ))}
    </div>
  );
}
